import React, { useRef, Ref, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Paper, Typography, Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleUp, faTrash, faSearch } from '@fortawesome/free-solid-svg-icons';
import { useModal } from '@lwtears/lwt-common-frontend/lib/Modal';
import { createEvent } from 'api';
import WorkbookPageModal from 'components/@common/Modals/WorkbookPageModal';
import ResourceModal from 'components/@common/Modals/ResourcePlayer';
import useDragDrop, { DragIndices } from 'state/hooks/ui/use-dragdrop';
import { AppStoreState } from 'types';
import { translate } from 'translations/translator';
import { getSwitchFrameType } from 'util/resources-util';
import { calcWorkbookPreviewSize } from 'util/lesson-plan-util';

const getTranslation = key => translate('lessonAssignment', key);

type Props = {
  handleRemove: () => void;
  handleSwap: (
    originIndices: { col: any; item: any },
    targetIndices: { col: any; item: any }
  ) => void;
  activity: {
    assigned: boolean;
    icon: string;
    id: number;
    data: { mediaType: string; fileExtension: string; src: string };
    duration: number;
    resourceId: number;
    resourceTypeId: number;
    title: string;
    type: {
      id: number;
      type: string;
      title: string;
      icon: string;
    };
    url: string;
  };
  index: number;
  item: {
    activities: any[];
    id: number;
    sectionIdx: number;
    title: string;
  };
  col: any;
};

const ActionGroup = ({
  handlePreviewResource,
  handleMoveUp,
  handleMoveDown,
  handleRemove,
  btnClass,
  btnClassDisabled,
  resourceType,
  ...props
}) => {
  return (
    <Box className={btnClass} {...props}>
      <button onClick={handlePreviewResource} className={btnClass}>
        <FontAwesomeIcon flip="horizontal" title="Preview Activity" icon={faSearch} size="sm" />
      </button>
      <button onClick={handleRemove} className={btnClass}>
        <FontAwesomeIcon title="Remove Activity From Assignment" icon={faTrash} size="sm" />
      </button>
      <Typography variant="body1" style={{ fontWeight: 300 }}>
        {getTranslation('reorder')}
      </Typography>
      <button onClick={handleMoveUp} className={btnClass}>
        <FontAwesomeIcon title="Move Activity Down in Order" icon={faAngleDown} size="sm" />
      </button>
      <button onClick={handleMoveDown} className={btnClass}>
        <FontAwesomeIcon title="Move Activity Up in Order" icon={faAngleUp} size="sm" />
      </button>
    </Box>
  );
};

export const LessonAssignmentActivityCard: React.FC<Props> = ({
  handleSwap,
  activity,
  index,
  item,
  handleRemove,
  col
}) => {
  const [, { openDraggableModal }] = useModal();
  const { align, flexItem, ...classes } = useStyles();
  const ref: Ref<any> = useRef(null);
  const dragIndices: DragIndices = { col: col || 0, itemId: item.id, item: index };
  const [{ didDrop, endDrag, isDragging, canDrop, isOver }, { drag, drop }] = useDragDrop({
    ref,
    dragIndices
  });
  const currentWorkbook: any = useSelector(({ app }: AppStoreState) => app.currentWorkbook);
  const handlePreviewResource = resource => {
    if (resource.type.type === 'workbookPage') {
      const { width } = calcWorkbookPreviewSize(
        window.innerHeight,
        currentWorkbook.orientation === 'portrait'
      );
      openDraggableModal({
        titleText: `${currentWorkbook.title} p. ${resource.pageNumber}`,
        body: (
          <WorkbookPageModal
            workbook={currentWorkbook}
            currentPage={resource.pageNumber}
            width={width}
          />
        )
      });
    } else {
      //make sure these two properties are at the root level
      //so ResourceModal can see them (versus buried in data)
      resource.aspectRatio = resource.data?.aspectRatio;
      resource.heightAdjust = resource.data?.heightAdjust;
      openDraggableModal({
        titleText: resource.title,
        body: (
          <ResourceModal
            selectedResource={resource}
            frameType={getSwitchFrameType(resource.type.type, resource.data.mediaType)}
            handleClose={() => {}}
            eventLogger={() =>
              createEvent('accessResource', {
                section: 'assignmentPreview',
                resourceId: resource.id,
                resourceType: resource.type.type
              })
            }
          />
        ),
        options: { closeOnBackdropClick: true },
        getPaperStyles: defaultStyles => ({
          ...defaultStyles,
          maxHeight: '85vh'
        })
      });
    }
  };

  useEffect(() => {
    drag(drop(ref));
  }, [drag, drop]);
  useEffect(() => {
    if (didDrop && Object.keys(endDrag).length && endDrag.canSwap) {
      // handleSwap(endDrag.items.originIndices, endDrag.items.targetIndices);
    }
  }, [didDrop, endDrag, handleSwap]);
  return (
    <Paper
      ref={ref}
      style={{
        border: canDrop && isOver ? '1px solid blue' : '#fff',
        opacity: isDragging ? 0 : 1,
        fontSize: 25,
        fontWeight: 'bold',
        cursor: 'move'
      }}
      className={classes.container}
      elevation={3}
    >
      <Box className={align} style={{ margin: '31px 24px' }}>
        {activity.type.icon && (
          <img src={activity.type.icon} className={classes.imgClass} alt={`icon type`} />
        )}
      </Box>
      <Box className={`${align} ${flexItem}`}>
        <Typography variant="subtitle1">{activity.title}</Typography>
        {/* get the resource type */}
        <Typography style={{ color: '#454545' }} variant="subtitle2">
          {getTranslation('activityType')}: {activity.type?.title}
        </Typography>
      </Box>

      <ActionGroup
        btnClass={classes.btnClass}
        btnClassDisabled={classes.btnClassDisabled}
        className={classes.actionGroup}
        handleMoveUp={() => handleSwap({ col, item: index }, { col, item: index + 1 })}
        handleMoveDown={() => handleSwap({ col, item: index }, { col, item: index - 1 })}
        handleRemove={handleRemove}
        handlePreviewResource={() => handlePreviewResource(activity)}
        resourceType={activity.type.type}
      />
    </Paper>
  );
};

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flex: 1,
    height: 112,
    margin: '24px 32px',
    justifyContent: 'space-between'
  },
  flexItem: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column'
  },
  actionGroup: {
    display: 'flex',
    flex: 0.3,
    justifyContent: 'space-evenly',
    alignItems: 'center'
  },
  align: {
    alignSelf: 'center'
  },
  btnClass: {
    border: '2px solid #000000',
    borderRadius: 3,
    height: 37,
    width: 37,
    margin: 10,
    userSelect: 'none',
    '&:focus': {
      outline: 'none',
      border: '2px solid #000000'
    },
    '&:active': {
      outline: 'none',
      border: '3px solid #000000'
    }
  },
  btnClassDisabled: {
    border: '2px solid #000000',
    borderRadius: 3,
    height: 37,
    width: 37,
    margin: 10,
    userSelect: 'none',
    disabled: 'true',
    opacity: 0.4
  },
  imgClass: {
    border: '3px solid #000000',
    borderRadius: '100%',
    height: 60,
    width: 60,
    padding: 4
  }
}));
